import { useState } from "react";
import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", moveInDate: "", stayLength: "", guests: "", reason: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in required fields");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        check_in: formData.moveInDate || "TBD",
        check_out: "Extended Stay",
        guests: parseInt(formData.guests) || 1,
        message: `Stay Length: ${formData.stayLength}\nReason: ${formData.reason}\n\n${formData.message}`
      });
      setIsSubmitted(true);
      toast.success("Thank you! We'll contact you within 24 hours.");
    } catch (error) {
      toast.error("Failed to send. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Contact & Book | Paradise Beach Rental Indialantic FL | Call (407) 450-9182"
        description="Request a quote for our furnished Indialantic beach house. Call or text (407) 450-9182 or send an inquiry — fast response, 90+ day stays, book direct and save."
        path="/contact"
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#0A1628]">
          <div className="container-premium text-center">
            <div className="gold-line mx-auto mb-6" />
            <span className="label-sm text-[#C9A962]">Contact</span>
            <h1 className="headline-xl text-white mt-4 mb-6">Get Your Custom Quote</h1>
            <p className="body-lg text-white/70 max-w-2xl mx-auto">
              Tell us about your extended stay needs. We respond within 24 hours.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="section-lg">
          <div className="container-premium">
            <div className="grid lg:grid-cols-5 gap-12">
              {/* Contact Info */}
              <div className="lg:col-span-2">
                <h2 className="headline-md text-[#0A1628] mb-6">Contact Direct</h2>
                <p className="body-md text-[#0A1628]/60 mb-8">
                  Skip the booking platforms. Work directly with us for better rates and personalized service.
                </p>

                <div className="space-y-6">
                  <a href="tel:4074509182" className="flex items-center gap-4 p-4 bg-white border border-[#EDE7DB] hover:border-[#C9A962] transition-colors" data-testid="contact-call-link">
                    <div className="w-12 h-12 bg-[#0A1628] rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-[#C9A962]" />
                    </div>
                    <div>
                      <p className="font-body font-semibold text-[#0A1628]">Call Us</p>
                      <p className="text-[#0A1628]/60">(407) 450-9182</p>
                    </div>
                  </a>
                  <a href="sms:4074509182?&body=Hi!%20I'm%20interested%20in%20a%2090%2B%20day%20stay%20at%20Beach%20Escape%20FL." className="flex items-center gap-4 p-4 bg-white border border-[#EDE7DB] hover:border-[#C9A962] transition-colors" data-testid="contact-text-link">
                    <div className="w-12 h-12 bg-[#0A1628] rounded-full flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-[#C9A962]" />
                    </div>
                    <div>
                      <p className="font-body font-semibold text-[#0A1628]">Text Us</p>
                      <p className="text-[#0A1628]/60">(407) 450-9182</p>
                    </div>
                  </a>
                  <a href="mailto:RAEVenturesLLC@gmail.com?subject=Extended%20Stay%20Inquiry&body=Hi%2C%20I%27d%20like%20more%20info%20on%20a%2090%2B%20day%20stay%20at%20Beach%20Escape%20FL." className="flex items-center gap-4 p-4 bg-white border border-[#EDE7DB] hover:border-[#C9A962] transition-colors" data-testid="contact-email-link">
                    <div className="w-12 h-12 bg-[#0A1628] rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#C9A962]" />
                    </div>
                    <div>
                      <p className="font-body font-semibold text-[#0A1628]">Email</p>
                      <p className="text-[#0A1628]/60 text-sm">RAEVenturesLLC@gmail.com</p>
                    </div>
                  </a>
                </div>

                <div className="mt-8 pt-8 border-t border-[#EDE7DB]">
                  <div className="flex items-start gap-3 text-[#0A1628]/60">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-body font-semibold text-[#0A1628]">Property Location</p>
                      <p className="text-sm">2161 Todd Ln, Indialantic, FL 32903</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-3">
                {isSubmitted ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-[#F7F3ED] p-12 text-center" data-testid="contact-success">
                    <CheckCircle className="w-16 h-16 text-[#C9A962] mx-auto mb-6" />
                    <h3 className="headline-md text-[#0A1628] mb-4">Thank You!</h3>
                    <p className="text-[#0A1628]/60 mb-8">We've received your inquiry and will respond within 24 hours.</p>
                    <Button onClick={() => setIsSubmitted(false)} className="bg-[#0A1628] text-white hover:bg-[#1E3A5F] rounded-none px-8 py-4" data-testid="submit-another-btn">
                      Submit Another
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-[#F7F3ED] p-8 lg:p-10" data-testid="contact-form">
                    <h3 className="font-display text-2xl text-[#0A1628] mb-6">Request a Quote</h3>
                    
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-sm text-[#0A1628]/60 mb-2 block">Name *</Label>
                        <Input name="name" value={formData.name} onChange={handleInputChange} placeholder="Your name" className="h-12 rounded-none border-[#D4C9B8]" required data-testid="contact-name-input" />
                      </div>
                      <div>
                        <Label className="text-sm text-[#0A1628]/60 mb-2 block">Email *</Label>
                        <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" className="h-12 rounded-none border-[#D4C9B8]" required data-testid="contact-email-input" />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-sm text-[#0A1628]/60 mb-2 block">Phone</Label>
                        <Input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="(555) 123-4567" className="h-12 rounded-none border-[#D4C9B8]" data-testid="contact-phone-input" />
                      </div>
                      <div>
                        <Label className="text-sm text-[#0A1628]/60 mb-2 block">Desired Move-in</Label>
                        <Input name="moveInDate" type="date" value={formData.moveInDate} onChange={handleInputChange} className="h-12 rounded-none border-[#D4C9B8]" data-testid="contact-movein-input" />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-sm text-[#0A1628]/60 mb-2 block">Length of Stay</Label>
                        <Select value={formData.stayLength} onValueChange={(v) => handleSelectChange("stayLength", v)}>
                          <SelectTrigger className="h-12 rounded-none border-[#D4C9B8]" data-testid="contact-staylength-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3-months">3 Months</SelectItem>
                            <SelectItem value="6-months">6 Months</SelectItem>
                            <SelectItem value="seasonal">Seasonal</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-[#0A1628]/60 mb-2 block">Reason for Stay</Label>
                        <Select value={formData.reason} onValueChange={(v) => handleSelectChange("reason", v)}>
                          <SelectTrigger className="h-12 rounded-none border-[#D4C9B8]" data-testid="contact-reason-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="corporate">Corporate</SelectItem>
                            <SelectItem value="travel-nurse">Travel Nurse</SelectItem>
                            <SelectItem value="snowbird">Snowbird</SelectItem>
                            <SelectItem value="insurance">Insurance</SelectItem>
                            <SelectItem value="renovation">Renovation</SelectItem>
                            <SelectItem value="remote-work">Remote Work</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="mb-6">
                      <Label className="text-sm text-[#0A1628]/60 mb-2 block">Additional Details</Label>
                      <Textarea name="message" value={formData.message} onChange={handleInputChange} placeholder="Tell us about your housing needs..." className="min-h-[120px] rounded-none border-[#D4C9B8] resize-none" data-testid="contact-message-input" />
                    </div>

                    <Button type="submit" disabled={isSubmitting} className="w-full bg-[#0A1628] text-white hover:bg-[#1E3A5F] rounded-none h-14 text-base relative z-10" data-testid="contact-submit-btn">
                      {isSubmitting ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Inquiry</>}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />
    </div>
  );
};

export default ContactPage;
